import React, { useEffect, useRef } from "react";
import Editor from "@monaco-editor/react";
import Button from "../ui/Button";
import Modal from "../ui/Modal";

const formatDetails = (details) =>
  details == null ? "" : JSON.stringify(details, null, 2);

export default function ExtractDataModal({
  details,
  isOpen,
  onClose,
  onSave,
  title = "Extracted Data",
}) {
  const editorRef = useRef(null);
  const textRef = useRef("");
  const detailsRef = useRef(details);
  detailsRef.current = details;

  // Seed the fallback text once per open, before Monaco mounts.
  // Do not refresh it while the modal stays open, or a parent re-render
  // would wipe pasted edits.
  useEffect(() => {
    if (!isOpen) {
      editorRef.current = null;
      return;
    }
    if (editorRef.current) return;
    textRef.current = formatDetails(detailsRef.current);
  }, [isOpen]);

  const handleMount = (editor) => {
    editorRef.current = editor;
    const formatted = formatDetails(detailsRef.current);
    textRef.current = formatted;
    if (editor.getValue() !== formatted) editor.setValue(formatted);
  };

  const handleSave = () => {
    const raw = editorRef.current?.getValue?.() ?? textRef.current;
    try {
      const parsedData = JSON.parse(raw);
      onSave?.(parsedData);
      onClose();
    } catch {
      alert("Invalid JSON. Please fix errors.");
    }
  };

  return (
    <Modal isOpen={isOpen} title={title} size="xl" onClose={onClose}>
      <div className="d-flex flex-column gap-3" style={{ height: "80vh" }}>
        <div style={{ flex: 1, border: "1px solid #ddd", overflow: "hidden" }}>
          <Editor
            height="100%"
            defaultLanguage="json"
            defaultValue={formatDetails(details)}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              wordWrap: "on",
              automaticLayout: true,
              formatOnPaste: false,
              formatOnType: false,
            }}
            onMount={handleMount}
            onChange={(value) => {
              textRef.current = value ?? "";
            }}
          />
        </div>
        <div className="action-container mb-3">
          <Button variant="outline" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleSave}>
            Save JSON
          </Button>
        </div>
      </div>
    </Modal>
  );
}
